/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * SUPERVISORY EFFECTIVENESS (v1_394).
 *
 * WHY THIS EXISTS. Until this build the engine treated supervision as a yes or
 * no. A VQF-supervised Swiss firm, an FCA-authorised firm and a VASP registered
 * somewhere with no functioning supervisor all scored identically on customer
 * and entity risk, which carries 25% of the tier. The words "AML-supervised"
 * were doing all the work and were evidenced by nothing but the applicant
 * saying so.
 *
 * WHAT THIS IS. FATF Immediate Outcome 3 from the consolidated assessment
 * ratings table: "Supervisors appropriately supervise, monitor and regulate
 * financial institutions, DNFBPs and VASPs for compliance with AML/CFT
 * requirements commensurate with their risks." One published four-level rating
 * per jurisdiction, from the peer review that assessed it.
 *
 * It is a CITATION, not a judgement of ours. An MLRO can put "FATF rated this
 * jurisdiction's supervision Substantial in its 2022 evaluation" in front of an
 * auditor. They cannot do that with a tier a software vendor invented.
 *
 * WHAT IT IS NOT. This is not a view on the country's money laundering risk,
 * which is geography and belongs to Layers 1 and 3. It bears on ONE claim: how
 * much comfort the fact of being supervised there is worth.
 *
 * ONE CAUTION FOR ANYONE READING THE TABLE. The ratings will correct
 * assumptions. Ireland is Substantial while the United Kingdom and the United
 * States are Moderate. Bermuda, Malaysia and Qatar are Substantial. Denmark,
 * Finland, Iceland, Malta and Monaco are Low. That is the point: it is evidence
 * rather than an impression of which regulators sound serious.
 *
 * SOURCE. FATF and FSRB consolidated assessment ratings, position as at
 * 23 December 2025. Ratings are updated as follow-up reports are published, so
 * this table needs refreshing against the FATF publication periodically; the
 * stamp below is what any report should cite.
 */

export const IO3_SOURCE = 'FATF and FSRB consolidated assessment ratings, Immediate Outcome 3 (supervision)';
export const IO3_AS_OF = '2025-12-23';

/** How stale is too stale to present without saying so? */
const STALE_AFTER_DAYS = 400;

export function io3IsStale(now) {
  const days = ((now ? new Date(now) : new Date()) - new Date(IO3_AS_OF)) / 86400000;
  return days > STALE_AFTER_DAYS;
}

/**
 * [jurisdiction, IO3 rating, report date, assessing body]
 * HE high, SE substantial, ME moderate, LE low.
 */
const RATINGS = [
  ['Albania', 'ME', 'May/23', 'MONEYVAL'],
  ['Algeria', 'LE', 'May/25', 'MENAFATF'],
  ['Andorra', 'ME', 'Dec/21', 'MONEYVAL'],
  ['Angola', 'LE', 'Jun/23', 'ESAAMLG'],
  ['Anguilla', 'ME', 'Jul/24', 'CFATF'],
  ['Antigua and Barbuda', 'LE', 'Nov/21', 'CFATF'],
  ['Argentina', 'ME', 'Dec/24', 'FATF/GAFILAT'],
  ['Armenia', 'ME', 'Jul/18', 'MONEYVAL'],
  ['Aruba', 'SE', 'Jul/22', 'CFATF'],
  ['Australia', 'ME', 'Mar/24', 'FATF'],
  ['Austria', 'ME', 'Nov/18', 'FATF'],
  ['Azerbaijan', 'LE', 'Feb/24', 'MONEYVAL'],
  ['Bahamas', 'ME', 'Dec/22', 'CFATF'],
  ['Bahrain', 'SE', 'May/22', 'MENAFATF'],
  ['Bangladesh', 'ME', 'Dec/20', 'APG'],
  ['Barbados', 'ME', 'Feb/21', 'CFATF'],
  ['Belarus', 'ME', 'Dec/19', 'EAG'],
  ['Belgium', 'ME', 'Sep/18', 'FATF'],
  ['Belize', 'ME', 'Jan/25', 'CFATF'],
  ['Benin', 'LE', 'May/25', 'GIABA'],
  ['Bermuda', 'SE', 'Jan/20', 'CFATF'],
  ['Bhutan', 'LE', 'Aug/23', 'APG'],
  ['Bolivia', 'ME', 'Aug/25', 'GAFILAT'],
  ['Bosnia and Herzegovina', 'ME', 'Dec/24', 'MONEYVAL'],
  ['Botswana', 'LE', 'Jan/23', 'ESAAMLG'],
  ['Brazil', 'ME', 'Dec/23', 'FATF/GAFILAT'],
  ['British Virgin Islands', 'LE', 'Feb/24', 'CFATF'],
  ['Brunei Darussalam', 'ME', 'Jun/24', 'APG'],
  ['Bulgaria', 'ME', 'May/24', 'MONEYVAL'],
  ['Burkina Faso', 'LE', 'May/25', 'GIABA'],
  ['Cabo Verde', 'LE', 'May/25', 'GIABA'],
  ['Cambodia', 'LE', 'Oct/23', 'APG'],
  ['Cameroon', 'LE', 'Nov/24', 'GABAC'],
  ['Canada', 'SE', 'Sep/21', 'FATF'],
  ['Cayman Islands', 'LE', 'Oct/21', 'CFATF'],
  ['Central African Republic', 'LE', 'Nov/23', 'GABAC'],
  ['Chad', 'LE', 'Jul/23', 'GABAC'],
  ['Chile', 'ME', 'Sep/21', 'GAFILAT'],
  ['China', 'ME', 'Nov/22', 'FATF'],
  ['Chinese Taipei', 'ME', 'Oct/19', 'APG'],
  ['Colombia', 'ME', 'Jan/23', 'GAFILAT'],
  ['Comoros', 'LE', 'May/24', 'GIABA'],
  ['Congo', 'LE', 'Sep/22', 'GABAC'],
  ['Cook Islands', 'SE', 'Sep/18', 'APG'],
  ['Costa Rica', 'ME', 'Feb/24', 'GAFILAT'],
  ['Croatia', 'ME', 'Oct/24', 'MONEYVAL'],
  ['Cuba', 'SE', 'Feb/24', 'GAFILAT'],
  ['Curacao', 'LE', 'Jul/25', 'CFATF'],
  ['Cyprus', 'ME', 'Dec/23', 'MONEYVAL'],
  ['Czechia', 'ME', 'Dec/22', 'MONEYVAL'],
  ['Cote d Ivoire', 'LE', 'May/25', 'IMF/GIABA'],
  ['Democratic Republic of the Congo', 'LE', 'Apr/23', 'GABAC'],
  ['Denmark', 'LE', 'Feb/21', 'FATF'],
  ['Djibouti', 'LE', 'Nov/24', 'MENAFATF'],
  ['Dominica', 'ME', 'Jul/23', 'CFATF'],
  ['Dominican Republic', 'LE', 'Sep/19', 'GAFILAT'],
  ['Ecuador', 'ME', 'Jan/23', 'GAFILAT'],
  ['Egypt', 'ME', 'May/25', 'MENAFATF'],
  ['El Salvador', 'LE', 'Aug/24', 'GAFILAT'],
  ['Equatorial Guinea', 'LE', 'Nov/24', 'GABAC'],
  ['Estonia', 'ME', 'Dec/24', 'MONEYVAL'],
  ['Eswatini', 'LE', 'Aug/22', 'ESAAMLG'],
  ['Ethiopia', 'LE', 'Aug/22', 'ESAAMLG'],
  ['Fiji', 'ME', 'May/23', 'APG'],
  ['Finland', 'LE', 'Oct/23', 'FATF'],
  ['France', 'ME', 'May/22', 'FATF'],
  ['Gabon', 'LE', 'Jul/23', 'GABAC'],
  ['Gambia', 'LE', 'Oct/22', 'GIABA'],
  ['Georgia', 'ME', 'Oct/24', 'MONEYVAL'],
  ['Germany', 'ME', 'Dec/23', 'FATF'],
  ['Ghana', 'ME', 'Aug/22', 'GIABA'],
  ['Gibraltar', 'ME', 'May/24', 'MONEYVAL'],
  ['Greece', 'ME', 'Sep/19', 'FATF'],
  ['Grenada', 'ME', 'Jul/22', 'CFATF'],
  ['Guatemala', 'ME', 'Oct/18', 'CFATF/GAFILAT'],
  ['Guernsey', 'ME', 'Dec/24', 'MONEYVAL'],
  ['Guinea', 'LE', 'Jan/24', 'GIABA'],
  ['Guinea-Bissau', 'LE', 'May/22', 'GIABA'],
  ['Guyana', 'ME', 'Jul/24', 'CFATF'],
  ['Haiti', 'LE', 'Jul/19', 'CFATF'],
  ['Holy See', 'SE', 'May/24', 'MONEYVAL'],
  ['Honduras', 'ME', 'Jan/20', 'GAFILAT'],
  ['Hong Kong', 'ME', 'Feb/23', 'FATF'],
  ['Hungary', 'ME', 'May/24', 'MONEYVAL'],
  ['Iceland', 'LE', 'Sep/21', 'FATF'],
  ['India', 'ME', 'Sep/24', 'FATF/APG/EAG'],
  ['Indonesia', 'ME', 'May/25', 'FATF'],
  ['Iraq', 'ME', 'May/24', 'MENAFATF'],
  ['Ireland', 'SE', 'Feb/22', 'FATF'],
  ['Isle of Man', 'ME', 'Dec/22', 'MONEYVAL'],
  ['Israel', 'ME', 'May/22', 'FATF/MONEYVAL'],
  ['Italy', 'ME', 'Mar/19', 'IMF/FATF'],
  ['Jamaica', 'ME', 'Jan/24', 'CFATF'],
  ['Japan', 'ME', 'Oct/24', 'FATF/APG'],
  ['Jersey', 'ME', 'May/24', 'MONEYVAL'],
  ['Jordan', 'ME', 'May/25', 'MENAFATF'],
  ['Kazakhstan', 'ME', 'Jul/23', 'EAG'],
  ['Kenya', 'LE', 'Aug/24', 'ESAAMLG'],
  ['Korea', 'ME', 'Oct/24', 'FATF'],
  ['Kuwait', 'ME', 'Oct/24', 'FATF/MENAFATF'],
  ['Kyrgyzstan', 'ME', 'Dec/23', 'EAG'],
  ['Laos', 'LE', 'Oct/24', 'APG'],
  ['Latvia', 'ME', 'Dec/19', 'MONEYVAL'],
  ['Lebanon', 'ME', 'Dec/23', 'MENAFATF'],
  ['Lesotho', 'ME', 'Feb/24', 'ESAAMLG'],
  ['Liberia', 'LE', 'Jul/23', 'GIABA'],
  ['Liechtenstein', 'ME', 'Jul/22', 'MONEYVAL'],
  ['Lithuania', 'ME', 'Dec/23', 'MONEYVAL'],
  ['Luxembourg', 'ME', 'Sep/23', 'FATF'],
  ['Macau', 'SE', 'Oct/19', 'APG'],
  ['Madagascar', 'LE', 'Apr/24', 'ESAAMLG/WB'],
  ['Malawi', 'ME', 'Apr/24', 'ESAAMLG'],
  ['Malaysia', 'SE', 'Oct/18', 'APG/FATF'],
  ['Maldives', 'LE', 'Oct/25', 'APG'],
  ['Mali', 'LE', 'Jan/24', 'GIABA'],
  ['Malta', 'LE', 'Aug/21', 'MONEYVAL'],
  ['Marshall Islands', 'ME', 'Nov/24', 'APG'],
  ['Mauritania', 'LE', 'Nov/21', 'MENAFATF'],
  ['Mauritius', 'LE', 'Jan/23', 'ESAAMLG'],
  ['Mexico', 'ME', 'May/23', 'FATF'],
  ['Moldova', 'ME', 'May/24', 'MONEYVAL'],
  ['Monaco', 'LE', 'Nov/24', 'MONEYVAL'],
  ['Mongolia', 'LE', 'Oct/23', 'APG'],
  ['Montenegro', 'ME', 'Jan/24', 'MONEYVAL'],
  ['Montserrat', 'ME', 'Jan/25', 'CFATF'],
  ['Morocco', 'ME', 'May/24', 'MENAFATF'],
  ['Mozambique', 'LE', 'Jul/23', 'ESAAMLG'],
  ['Myanmar', 'LE', 'Oct/24', 'APG'],
  ['Namibia', 'LE', 'Apr/24', 'ESAAMLG'],
  ['Nauru', 'LE', 'Feb/25', 'APG'],
  ['Nepal', 'LE', 'Jun/24', 'APG'],
  ['Netherlands', 'ME', 'Sep/25', 'FATF'],
  ['New Zealand', 'ME', 'Jul/24', 'FATF/APG'],
  ['Nicaragua', 'LE', 'Jan/21', 'GAFILAT'],
  ['Niger', 'LE', 'Nov/21', 'GIABA'],
  ['Nigeria', 'ME', 'Nov/24', 'GIABA'],
  ['Niue', 'LE', 'Oct/25', 'APG'],
  ['North Macedonia', 'ME', 'Jul/23', 'MONEYVAL'],
  ['Norway', 'ME', 'Feb/23', 'FATF'],
  ['Oman', 'ME', 'Dec/24', 'FATF/MENAFATF'],
  ['Pakistan', 'LE', 'Aug/22', 'APG'],
  ['Palau', 'ME', 'Jun/23', 'APG'],
  ['Panama', 'ME', 'Aug/19', 'GAFILAT'],
  ['Papua New Guinea', 'ME', 'Sep/24', 'APG'],
  ['Paraguay', 'ME', 'Nov/22', 'GAFILAT'],
  ['Peru', 'ME', 'Jan/20', 'GAFILAT'],
  ['Philippines', 'ME', 'Aug/22', 'APG'],
  ['Poland', 'ME', 'Oct/24', 'MONEYVAL'],
  ['Portugal', 'ME', 'Dec/17', 'FATF'],
  ['Qatar', 'SE', 'May/23', 'FATF/MENAFATF'],
  ['Romania', 'ME', 'Jul/23', 'MONEYVAL'],
  ['Russia', 'ME', 'Feb/24', 'EAG'],
  ['Rwanda', 'LE', 'Jul/24', 'ESAAMLG'],
  ['Saint Kitts and Nevis', 'LE', 'Feb/22', 'CFATF'],
  ['Saint Lucia', 'LE', 'Feb/24', 'CFATF'],
  ['Saint Vincent and the Grenadines', 'ME', 'Jan/24', 'CFATF'],
  ['Samoa', 'LE', 'Jun/23', 'APG'],
  ['San Marino', 'ME', 'May/24', 'MONEYVAL'],
  ['Sao Tome and Principe', 'LE', 'Nov/24', 'GIABA'],
  ['Saudi Arabia', 'SE', 'Jan/20', 'FATF/MENAFATF'],
  ['Senegal', 'LE', 'Nov/24', 'GIABA'],
  ['Serbia', 'ME', 'Mar/24', 'MONEYVAL'],
  ['Seychelles', 'LE', 'Apr/24', 'ESAAMLG'],
  ['Sierra Leone', 'LE', 'Nov/24', 'GIABA'],
  ['Singapore', 'ME', 'Nov/19', 'FATF/APG'],
  ['Sint Maarten', 'LE', 'Jul/25', 'CFATF'],
  ['Slovakia', 'ME', 'Dec/24', 'MONEYVAL'],
  ['Slovenia', 'ME', 'Dec/23', 'MONEYVAL'],
  ['Solomon Islands', 'LE', 'Oct/19', 'APG'],
  ['South Africa', 'ME', 'Nov/24', 'FATF'],
  ['Spain', 'SE', 'Dec/19', 'FATF'],
  ['Sri Lanka', 'LE', 'Nov/21', 'APG'],
  ['Suriname', 'LE', 'Nov/23', 'CFATF'],
  ['Sweden', 'ME', 'Sep/20', 'FATF'],
  ['Switzerland', 'ME', 'Oct/23', 'FATF'],
  ['Tajikistan', 'ME', 'Nov/24', 'EAG'],
  ['Tanzania', 'LE', 'Sep/23', 'ESAAMLG'],
  ['Thailand', 'ME', 'Oct/23', 'APG'],
  ['Timor-Leste', 'LE', 'Nov/24', 'APG'],
  ['Togo', 'LE', 'May/25', 'GIABA'],
  ['Tonga', 'LE', 'Sep/21', 'APG'],
  ['Trinidad and Tobago', 'ME', 'Jun/19', 'CFATF'],
  ['Tunisia', 'LE', 'Jan/20', 'MENAFATF'],
  ['Turkey', 'ME', 'Jul/23', 'FATF'],
  ['Turkmenistan', 'ME', 'Jul/23', 'EAG'],
  ['Turks and Caicos Islands', 'LE', 'Nov/23', 'CFATF'],
  ['Uganda', 'LE', 'Sep/23', 'ESAAMLG'],
  ['Ukraine', 'ME', 'Sep/20', 'MONEYVAL'],
  ['United Arab Emirates', 'ME', 'Jul/23', 'MENAFATF'],
  ['United Kingdom', 'ME', 'Jun/22', 'FATF'],
  ['United States', 'ME', 'Mar/24', 'FATF'],
  ['Uruguay', 'ME', 'Jan/20', 'GAFILAT'],
  ['Uzbekistan', 'ME', 'Jul/23', 'EAG'],
  ['Vanuatu', 'LE', 'Sep/18', 'APG'],
  ['Venezuela', 'LE', 'Mar/23', 'CFATF'],
  ['Vietnam', 'LE', 'Feb/25', 'APG'],
  ['Zambia', 'ME', 'Sep/23', 'ESAAMLG'],
  ['Zimbabwe', 'LE', 'Apr/24', 'ESAAMLG']
];

/** Aliases and short forms that appear in real files. */
const ALIASES = {
  'uk': 'United Kingdom', 'great britain': 'United Kingdom', 'england': 'United Kingdom',
  'scotland': 'United Kingdom', 'wales': 'United Kingdom', 'northern ireland': 'United Kingdom',
  'usa': 'United States', 'us': 'United States', 'united states of america': 'United States',
  'uae': 'United Arab Emirates', 'emirates': 'United Arab Emirates', 'dubai': 'United Arab Emirates',
  'abu dhabi': 'United Arab Emirates',
  'bvi': 'British Virgin Islands', 'virgin islands british': 'British Virgin Islands',
  'hong kong china': 'Hong Kong', 'hongkong': 'Hong Kong',
  'macau china': 'Macau', 'macao': 'Macau',
  'south korea': 'Korea', 'republic of korea': 'Korea',
  'czech republic': 'Czechia', 'slovak republic': 'Slovakia',
  'turkiye': 'Turkey', 'holland': 'Netherlands', 'the netherlands': 'Netherlands',
  'viet nam': 'Vietnam', 'lao': 'Laos', 'lao people s democratic republic': 'Laos',
  'bailiwick of guernsey': 'Guernsey', 'bailiwick of jersey': 'Jersey',
  'peoples republic of china': 'China', 'china people s republic of': 'China',
  'vatican': 'Holy See', 'vatican city': 'Holy See',
  'st kitts and nevis': 'Saint Kitts and Nevis', 'st lucia': 'Saint Lucia',
  'st vincent and the grenadines': 'Saint Vincent and the Grenadines',
  'ivory coast': 'Cote d Ivoire', 'cote divoire': 'Cote d Ivoire',
  'swaziland': 'Eswatini', 'burma': 'Myanmar',
  'russian federation': 'Russia', 'republic of ireland': 'Ireland',
  'papua new guinea png': 'Papua New Guinea', 'tci': 'Turks and Caicos Islands'
};

import { canonicalPlace } from './country-canonical.js';

function norm(v) {
  // Canonical form first (Republic of Mauritius -> Mauritius, UAE -> United
  // Arab Emirates), then fold. A rated jurisdiction must never read as
  // unassessed because the model wrote its long-form name (07/08/26).
  return String(canonicalPlace(v) || '')
    .replace(/\s*\(.*$/, '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\bthe\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const INDEX = new Map();
for (const [name, rating, date, body] of RATINGS) {
  const rec = { jurisdiction: name, rating, reportDate: date, assessedBy: body };
  INDEX.set(norm(name), rec);
  INDEX.set(norm(name).replace(/\s+/g, ''), rec);
}

/**
 * Look up the supervision effectiveness rating for a jurisdiction.
 * Returns null when the jurisdiction has not been assessed, which is reported
 * honestly rather than treated as a pass or a fail.
 */
/**
 * Every jurisdiction the dataset rates LOW effectiveness, for deriving the
 * country-policy catalogue (v1_457): the picker must never again be missing a
 * jurisdiction the platform itself holds a published low-effectiveness
 * finding for. Mauritius was in the curated list; forty-five others were not.
 */
export function lowEffectivenessJurisdictions() {
  return RATINGS.filter(r => r[1] === 'LE').map(([jurisdiction, rating, reportDate, assessedBy]) => ({ jurisdiction, reportDate, assessedBy }));
}

export function supervisionRating(jurisdiction) {
  const key = norm(jurisdiction);
  if (!key) return null;
  const alias = ALIASES[key] || ALIASES[key.replace(/\s+/g, ' ')];
  const rec = INDEX.get(norm(alias || jurisdiction)) || INDEX.get(key) || INDEX.get(key.replace(/\s+/g, ''));
  return rec || null;
}

/**
 * WHICH JURISDICTIONS DOES A PIECE OF FREE TEXT ACTUALLY NAME? (v1_539)
 *
 * The activity profile records geographies as PROSE ("Canada (FINTRAC MSB),
 * UK, EU, with cross-border remittance/payments; group platform clients
 * include counterparties in..."), and the map's claimed-market check used to
 * split that prose on commas and demand a document containing each fragment
 * verbatim — so "with cross-border remittance/payments" became a "market" no
 * document could ever evidence, and the amber could never clear (Snowcap,
 * 13/08/26). Recognition replaces splitting: only names this table (or its
 * alias map) recognises count as claimed markets, and the SAME recogniser
 * reads the documents, so both sides of the comparison speak one language.
 * A word the table does not recognise ("EU", a business description) is
 * simply not a claim this check asserts anything about — the line only ever
 * names jurisdictions, so it only ever asks for evidence that can exist.
 *
 * Matching rules, each one earned by a false positive:
 * - Text and names are normalised identically (lowercase, accents folded,
 *   punctuation to spaces) before comparison, so "Côte d'Ivoire" in a
 *   summary matches the table's spelling of it.
 * - Longest names match first and every match is struck from the text, so
 *   Papua New Guinea can never read as Guinea and the United Arab Emirates
 *   never as a bare Emirates.
 * - Aliases of three letters or fewer (UK, US, UAE, BVI, TCI) match only as
 *   UPPERCASE words in the original text: "tell us more" must never become
 *   the United States.
 * - "New Jersey" and "South Georgia" are struck before scanning: a US state
 *   on a bank statement is not the Bailiwick of Jersey.
 * - Word boundaries throughout: Oman must not surface from Romania.
 *
 * Returns a Set of canonical jurisdiction names as the RATINGS table spells
 * them, so callers can compare two texts' mentions by simple set membership.
 */
let JM_MATCHERS = null;
const jmNorm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ');
export function jurisdictionMentions(text) {
  if (!JM_MATCHERS) {
    const entries = [];
    for (const r of RATINGS) entries.push([r[0], r[0]]);
    for (const [alias, canon] of Object.entries(ALIASES)) entries.push([alias, canon]);
    JM_MATCHERS = entries
      .map(([namePart, canon]) => ({ canon, orig: namePart, needle: ' ' + jmNorm(namePart).trim() + ' ', short: namePart.replace(/[^a-z]/gi, '').length <= 3 }))
      .filter(m => m.needle.trim().length > 1)
      .sort((a, b) => b.needle.length - a.needle.length);
  }
  const raw = ' ' + String(text || '') + ' ';
  let t = ' ' + jmNorm(raw).replace(/\bnew jersey\b|\bsouth georgia\b/g, ' ') + ' ';
  const found = new Set();
  for (const m of JM_MATCHERS) {
    if (m.short) {
      if (new RegExp('(^|[^A-Z0-9])' + m.orig.toUpperCase() + '($|[^A-Z0-9])').test(raw)) { found.add(m.canon); t = t.split(m.needle).join(' '); }
      continue;
    }
    if (t.indexOf(m.needle) < 0) continue;
    found.add(m.canon);
    t = t.split(m.needle).join(' ');
  }
  return found;
}

/** Long-form label for a rating code. */
export const IO3_LABEL = {
  HE: 'high effectiveness',
  SE: 'substantial effectiveness',
  ME: 'moderate effectiveness',
  LE: 'low effectiveness'
};

/**
 * How much is being supervised HERE worth?
 *
 * full     the supervision claim stands as a mitigant, customer risk Low
 * partial  the claim is worth something but not full comfort, customer Medium
 * none     supervision in a jurisdiction FATF rated ineffective evidences little
 * unknown  not assessed; no uplift, and the file says so
 */
export function supervisionWeight(jurisdiction) {
  const rec = supervisionRating(jurisdiction);
  if (!rec) return { weight: 'unknown', record: null };
  if (rec.rating === 'HE' || rec.rating === 'SE') return { weight: 'full', record: rec };
  if (rec.rating === 'ME') return { weight: 'partial', record: rec };
  return { weight: 'none', record: rec };
}

/** The sentence that goes on the board and into the report. */
export function supervisionBasis(jurisdiction) {
  const rec = supervisionRating(jurisdiction);
  if (!rec) {
    return 'supervision in ' + (jurisdiction || 'this jurisdiction') + ' has not been assessed by FATF or an FSRB, so the fact of supervision carries no evidenced weight here';
  }
  return 'FATF rated supervision in ' + rec.jurisdiction + ' as ' + IO3_LABEL[rec.rating] +
    ' (' + rec.assessedBy + ', ' + rec.reportDate + ')';
}
